import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import {
  categories,
  subcategories,
  categoryNames,
  subcategoryNames,
  getMoviesByCategory,
  getMoviesBySubcategory,
} from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState<string>('all');

  const category = categories.find((c) => c.slug === slug);
  const subCats = slug ? subcategories[slug] || [] : [];
  const categoryName = slug ? categoryNames[slug] || slug : '';

  useEffect(() => {
    setActiveSub('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const displayMovies: MovieItem[] = (() => {
    if (!slug) return [];
    if (activeSub === 'all') return getMoviesByCategory(slug);
    return getMoviesBySubcategory(slug, activeSub);
  })();

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <p className="text-foreground-400">分类不存在</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-400 hover:text-primary-300 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />

          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
              <i className={`${category.icon} text-lg text-primary-400`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-100">
              {categoryName}
            </h1>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            <button
              onClick={() => setActiveSub('all')}
              className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                activeSub === 'all'
                  ? 'bg-primary-500 text-background-950 font-semibold'
                  : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {subCats.map((sub) => (
              <button
                key={sub.slug}
                onClick={() => setActiveSub(sub.slug)}
                className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                  activeSub === sub.slug
                    ? 'bg-primary-500 text-background-950 font-semibold'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                }`}
              >
                {sub.name}
              </button>
            ))}
          </div>

          {displayMovies.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-film-line text-2xl text-foreground-500"></i>
              </div>
              <p className="text-foreground-400">暂无相关内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-14">
              {displayMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="default" />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `牛牛影视 - ${categoryName}`,
            description: `牛牛影视${categoryName}频道，聚合最新最全的${categoryName}资源，高清在线观看。`,
            url: `https://www.niuniutv.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '牛牛影视',
              url: 'https://www.niuniutv.com',
            },
          }),
        }}
      />
    </>
  );
}